import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RichTextEditor } from '../components/RichTextEditor';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Megaphone, Send, RefreshCw, Users, CheckCircle2, AlertTriangle, Loader2, FlaskConical, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { parseApiError } from '../lib/parseApiError';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtData = (iso) => (iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '-');

export default function AvvisiClientiPage() {
  const { user } = useAuth();
  const [destinatari, setDestinatari] = useState([]);
  const [esclusi, setEsclusi] = useState(new Set());
  const [mostraLista, setMostraLista] = useState(false);
  const [oggetto, setOggetto] = useState('');
  const [messaggio, setMessaggio] = useState('');
  const [sending, setSending] = useState(false);
  const [storico, setStorico] = useState([]);

  const fetchDestinatari = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comunicazioni/destinatari`);
      setDestinatari(res.data.destinatari);
    } catch (e) {
      toast.error('Errore caricamento destinatari');
    }
  }, []);

  const fetchStorico = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comunicazioni`);
      setStorico(res.data);
    } catch (e) { /* silenzioso */ }
  }, []);

  useEffect(() => { fetchDestinatari(); fetchStorico(); }, [fetchDestinatari, fetchStorico]);

  // Auto-refresh dello storico mentre c'è un invio in corso
  useEffect(() => {
    if (!storico.some(s => s.stato === 'in_corso')) return;
    const t = setInterval(fetchStorico, 4000);
    return () => clearInterval(t);
  }, [storico, fetchStorico]);

  const selezionati = destinatari.filter(d => !esclusi.has(d.id));
  const messaggioVuoto = !messaggio.replace(/<[^>]*>/g, '').trim();

  const toggleCliente = (id) => {
    setEsclusi(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const [emailProva, setEmailProva] = useState('');
  const [sendingProva, setSendingProva] = useState(false);
  const [anteprimaHtml, setAnteprimaHtml] = useState(null);
  const [loadingAnteprima, setLoadingAnteprima] = useState(false);

  const handleAnteprima = async () => {
    if (!oggetto.trim() || messaggioVuoto) {
      toast.error("Compila oggetto e messaggio per vedere l'anteprima");
      return;
    }
    setLoadingAnteprima(true);
    try {
      const res = await axios.post(`${API}/comunicazioni/anteprima`, { oggetto, messaggio });
      setAnteprimaHtml(res.data.html);
    } catch (error) {
      toast.error(parseApiError(error, 'Errore anteprima'));
    } finally {
      setLoadingAnteprima(false);
    }
  };

  useEffect(() => { if (user?.email && !emailProva) setEmailProva(user.email); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProva = async () => {
    if (!oggetto.trim() || messaggioVuoto) {
      toast.error('Compila oggetto e messaggio prima di inviare la prova');
      return;
    }
    setSendingProva(true);
    try {
      const res = await axios.post(`${API}/comunicazioni/invia-prova`, { oggetto, messaggio, email: emailProva || undefined });
      toast.success(res.data.message || 'Email di prova inviata');
    } catch (error) {
      toast.error(parseApiError(error, "Errore invio prova"), { duration: 8000 });
    } finally {
      setSendingProva(false);
    }
  };

  const handleInvia = async () => {
    if (!oggetto.trim() || messaggioVuoto) {
      toast.error('Compila oggetto e messaggio');
      return;
    }
    if (selezionati.length === 0) {
      toast.error('Nessun destinatario selezionato');
      return;
    }
    if (!window.confirm(`Inviare l'avviso "${oggetto}" a ${selezionati.length} clienti?\n\nL'email partirà subito e non potrà essere annullata.`)) return;
    setSending(true);
    try {
      const body = { oggetto, messaggio };
      if (esclusi.size > 0) body.cliente_ids = selezionati.map(d => d.id);
      const res = await axios.post(`${API}/comunicazioni/invia`, body);
      toast.success(res.data.message);
      setOggetto('');
      setMessaggio('');
      setEsclusi(new Set());
      fetchStorico();
    } catch (error) {
      toast.error(parseApiError(error, "Errore nell'invio"), { duration: 8000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="avvisi-clienti-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
          <Megaphone className="h-8 w-8 text-cyan-500" />
          Avvisi ai Clienti
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Componi un'email (offerte commerciali, cambi di normativa, avvisi importanti) e inviala a tutti i clienti con email salvata
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compositore */}
        <Card className="rounded-md lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-mono uppercase">Componi avviso</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase">Oggetto *</Label>
              <Input
                value={oggetto}
                onChange={(e) => setOggetto(e.target.value)}
                placeholder="Es: Nuova normativa POS-RT dal 1° gennaio / Offerta speciale registratori"
                className="rounded-none"
                data-testid="avviso-oggetto"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Messaggio *</Label>
              <RichTextEditor
                value={messaggio}
                onChange={setMessaggio}
                placeholder={"Scrivi qui il testo dell'avviso... L'email partirà con il logo e i contatti della tua azienda, intestata a ogni cliente (\u201cGentile NOME CLIENTE\u201d)."}
                testId="avviso-messaggio"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAnteprima}
                disabled={loadingAnteprima || !oggetto.trim() || messaggioVuoto}
                className="rounded-none mt-2"
                data-testid="avviso-anteprima-btn"
              >
                {loadingAnteprima ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Anteprima email
              </Button>
            </div>
            <div className="border border-dashed border-cyan-400/60 bg-cyan-50/40 dark:bg-cyan-950/20 p-3 space-y-2">
              <Label className="text-xs font-mono uppercase flex items-center gap-1"><FlaskConical className="h-3.5 w-3.5" /> Prova prima di inviare a tutti</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  value={emailProva}
                  onChange={(e) => setEmailProva(e.target.value)}
                  placeholder="La tua email"
                  className="rounded-none sm:max-w-xs"
                  data-testid="avviso-email-prova"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleProva}
                  disabled={sendingProva || !oggetto.trim() || messaggioVuoto}
                  className="rounded-none border-cyan-500 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-950/40"
                  data-testid="avviso-prova-btn"
                >
                  {sendingProva ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-2" />}
                  Invia prova alla mia email
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Ricevi l'email esattamente come la vedranno i clienti (oggetto con prefisso [PROVA]). Nessun cliente riceverà nulla.</p>
            </div>
            <Button
              onClick={handleInvia}
              disabled={sending || selezionati.length === 0}
              className="rounded-none w-full md:w-auto"
              data-testid="avviso-invia-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Invia a {selezionati.length} clienti
            </Button>
          </CardContent>
        </Card>

        {/* Destinatari */}
        <Card className="rounded-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-mono uppercase flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><Users className="h-4 w-4 text-cyan-500" /> Destinatari</span>
              <Badge variant="outline" className="text-xs" data-testid="destinatari-count">
                {selezionati.length} / {destinatari.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Tutti i clienti con email sono selezionati. Puoi escluderne qualcuno.
            </p>
            <Button variant="outline" size="sm" className="rounded-none w-full text-xs" onClick={() => setMostraLista(v => !v)} data-testid="toggle-lista-destinatari">
              {mostraLista ? 'Nascondi lista' : 'Mostra / modifica lista'}
            </Button>
            {mostraLista && (
              <div className="border max-h-[320px] overflow-y-auto divide-y">
                {destinatari.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-muted/50">
                    <Checkbox
                      checked={!esclusi.has(d.id)}
                      onCheckedChange={() => toggleCliente(d.id)}
                      data-testid={`destinatario-check-${d.id}`}
                    />
                    <span className="flex-1 truncate font-medium">{d.ragione_sociale}</span>
                    <span className="text-muted-foreground truncate max-w-[140px]">{d.email}</span>
                  </label>
                ))}
                {destinatari.length === 0 && (
                  <p className="text-xs text-muted-foreground p-3">Nessun cliente con email in anagrafica.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Storico */}
      <Card className="rounded-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase flex items-center justify-between">
            <span>Storico avvisi inviati</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={fetchStorico} title="Aggiorna">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="storico-avvisi">
              <thead>
                <tr className="border-b text-muted-foreground font-mono uppercase text-[10px]">
                  <th className="text-left py-1.5 pr-2">Data</th>
                  <th className="text-left py-1.5 px-2">Oggetto</th>
                  <th className="text-right py-1.5 px-2">Destinatari</th>
                  <th className="text-right py-1.5 px-2">Inviati</th>
                  <th className="text-right py-1.5 px-2">Falliti</th>
                  <th className="text-left py-1.5 pl-2">Stato</th>
                </tr>
              </thead>
              <tbody>
                {storico.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted-foreground py-6">Nessun avviso inviato finora</td></tr>
                )}
                {storico.map((s) => (
                  <tr key={s.id} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="py-2 pr-2 whitespace-nowrap">{fmtData(s.created_at)}</td>
                    <td className="py-2 px-2 font-medium max-w-[300px] truncate">{s.oggetto}</td>
                    <td className="py-2 px-2 text-right font-mono">{s.tot_destinatari}</td>
                    <td className="py-2 px-2 text-right font-mono text-emerald-600">{s.inviati}</td>
                    <td className={`py-2 px-2 text-right font-mono ${s.falliti > 0 ? 'text-red-600 font-bold' : 'text-muted-foreground'}`}>{s.falliti}</td>
                    <td className="py-2 pl-2">
                      {s.stato === 'in_corso' ? (
                        <Badge className="text-[10px] bg-sky-500 text-white"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> In corso</Badge>
                      ) : s.falliti > 0 ? (
                        <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500"><AlertTriangle className="h-3 w-3 mr-1" /> Con errori</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-500"><CheckCircle2 className="h-3 w-3 mr-1" /> Completato</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Anteprima email formattata (senza invio) */}
      <Dialog open={!!anteprimaHtml} onOpenChange={(open) => { if (!open) setAnteprimaHtml(null); }}>
        <DialogContent className="sm:max-w-3xl rounded-none max-h-[92vh] flex flex-col" data-testid="anteprima-email-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-mono uppercase tracking-wider text-base flex items-center gap-2">
              <Eye className="h-4 w-4" /> Anteprima email — così la vedrà il cliente
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 border border-border bg-white">
            <iframe
              title="Anteprima email"
              srcDoc={anteprimaHtml || ''}
              sandbox=""
              className="w-full h-[70vh]"
              data-testid="anteprima-email-iframe"
            />
          </div>
          <p className="text-[11px] text-muted-foreground shrink-0">Nessuna email è stata inviata. "NOME CLIENTE (esempio)" verrà sostituito con la ragione sociale di ogni cliente.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
